import {
  RequestInterceptor,
  HttpRequest,
  HttpResponse,
  ResponseMapper
} from './interfaces';
import composeResponseMappers from './composeResponseMappers';
import InterceptorType from './InterceptorType';
import copyRequest from './copyRequest';
import { InterceptorDisabled } from './errors';
import { dispatchInterceptorChangeEvent } from '../events/dispatch';

export default class InterceptorStack implements RequestInterceptor {
  stack: RequestInterceptor[];
  responseMapper: ResponseMapper | undefined;
  interceptorType = InterceptorType.COMPOSITE;
  shouldReport: boolean;

  constructor(shouldReport?: boolean) {
    this.stack = [];
    this.shouldReport = shouldReport || false;
  }

  putInterceptor(interceptor: RequestInterceptor): void {
    // unshift puts the value on the front of the array.
    this.stack.unshift(interceptor);
    this.signalInterceptorChange(interceptor, true);
  }

  removeInterceptor(interceptor: RequestInterceptor): void {
    this.stack = this.stack.filter(i => i !== interceptor);
    this.signalInterceptorChange(interceptor, false);
  }

  signalInterceptorChange(interceptor: RequestInterceptor, add: boolean): void {
    if (this.shouldReport && interceptor.interceptorName) {
      const name = interceptor.interceptorName;
      dispatchInterceptorChangeEvent({ add, name });
    }
  }

  registerResponseMapper(mapper: ResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  async handle(request: HttpRequest): Promise<HttpResponse> {
    const toRemove: RequestInterceptor[] = [];
    for (const interceptor of this.stack) {
      try {
        // Return the first available response
        const resp = await interceptor.handle(copyRequest(request));
        if (this.responseMapper) {
          return Promise.resolve(this.responseMapper(resp));
        } else {
          return Promise.resolve(resp);
        }
      } catch (e) {
        if (e && (e as InterceptorDisabled).interceptorDisabled) {
          toRemove.push(interceptor);
        }
      }
    }
    // if any interceptors are disabled, remove them.
    toRemove.forEach(i => this.removeInterceptor(i));
    // If we make it here, then it's bad.
    return Promise.reject({
      status: 0,
      request,
      url: request.url
    });
  }
}
